import { FC, memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from '@crew/modules/i18n'
import {
  FormValues,
  useEditEventRecordingTranscribesEntryForm,
} from './useEditEventRecordingTranscribesEntryForm'
import { CrewSelectBoxField } from 'components/forms/crewSelectBoxField'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { UserRef } from '@crew/models/refs'
import { ComponentCallbackHandler } from '@crew/utils'
import { ValueChangedEvent } from 'devextreme/ui/select_box'

export type EditEventRecordingTranscribe = {
  eventRecordingTranscribeId: string
  version: number
}

// 定数を定義
const TEMP_USER_ID_PREFIX = 'temp-'

export type EditEventRecordingTranscribesEntryFormProps = {
  onSubmit: () => void
  onClose: () => void
  selectedTranscribes: EditEventRecordingTranscribe[]
  projectId: string | undefined // プロジェクトID（話者変更時のドロップダウンにプロジェクトメンバーを出すために必要）
}

// 話者一括変更フォーム
export const EditEventRecordingTranscribesEntryForm: FC<EditEventRecordingTranscribesEntryFormProps> =
  memo((props) => {
    const {
      control,
      formState,
      handleSubmit,
      setValue,
      getValues,
      validateRules,
      speakerDataSource,
      updateEventRecordingTranscribesSpeakers,
      isLoadingBulkUpdateEventRecordingTranscribes,
    } = useEditEventRecordingTranscribesEntryForm(props.projectId)

    const { t } = useTranslation()
    const toast = useToast()
    const [showApiErrors] = useShowApiErrors()
    const [selectedUser, setSelectedUser] = useState<UserRef | null>(null)

    const canSend = useMemo(
      // fromState.isValidはerrorsが空でもfalseになることがあるためerrorsで判定する
      () =>
        Object.keys(formState.errors).length === 0 && !formState.isSubmitting,
      // formStateはproxyなのでformState自体をlistenする必要がある
      // https://react-hook-form.com/api/useform/formstate
      [formState]
    )

    // 一時的なユーザー情報を作成する
    const createTempUser = useCallback((displayName: string) => {
      const tempUser: UserRef = {
        id: `${TEMP_USER_ID_PREFIX}${Date.now()}`,
        displayName,
        version: 1,
      }
      return tempUser
    }, [])

    // 登録ボタンクリック時
    const handleSubmitButtonClick = useCallback(() => {
      const onSubmit = async (data: FormValues) => {
        try {
          // APIリクエストを行うにあたり型変換
          const eventRecordingTranscribes: EditEventRecordingTranscribe[] =
            props.selectedTranscribes.map((transcribe) => ({
              eventRecordingTranscribeId: transcribe.eventRecordingTranscribeId,
              version: transcribe.version,
            }))

          // 話者一括更新APIを呼び出す
          await updateEventRecordingTranscribesSpeakers(
            eventRecordingTranscribes,
            data
          )

          // 成功時のトーストを表示
          toast.success(
            t('message.eventRecordingTranscribe.bulkUpdateSpeakersSuccess')
          )

          // onSubmitコールバックを呼び出す（現状は選択状態の解除のみ）
          props.onSubmit()

          // 登録後はダイアログを閉じるのでonCloseコールバックを呼び出す
          props.onClose()
        } catch (err) {
          showApiErrors(err)
        }
      }
      // 発言者をフリー入力した場合、SpeakerIdをnullにしてSpeakerNameをセットする
      const tempId = getValues('speakerId')
      if (tempId && tempId.startsWith(TEMP_USER_ID_PREFIX) && selectedUser) {
        setValue('speakerId', null)
        setValue('speakerName', selectedUser.displayName)
      }

      handleSubmit(onSubmit)()
    }, [
      getValues,
      selectedUser,
      handleSubmit,
      props,
      updateEventRecordingTranscribesSpeakers,
      toast,
      t,
      showApiErrors,
      setValue,
    ])

    // キャンセルボタンクリック時
    const handleCancelButtonClick = useCallback(() => {
      props.onClose()
    }, [props])

    // 発言者選択ボックスの値変更時
    const handleSpeakerIdChanged = useCallback(
      (event: ValueChangedEvent) => {
        const newSpeakerId = event.value as string
        if (!newSpeakerId) {
          // 選択中の発言者情報をリセットする
          setSelectedUser(null)
          return
        }

        // SpeakerIdがセットされた場合は、SpeakerNameをクリアする
        setValue('speakerName', null)

        // 選択中の発言者情報を保持する
        const selectedData = event.component.option('selectedItem') as UserRef
        setSelectedUser(selectedData)
      },
      [setValue]
    )

    // 発言者選択ボックスのカスタムアイテム作成時
    const handleSpeakerCustomItemCreating = useCallback<
      ComponentCallbackHandler<
        typeof CrewSelectBoxField,
        'onCustomItemCreating'
      >
    >(
      (args) => {
        if (!args.text) {
          return
        }

        // 発言者にフリー入力の値が選択された場合は、一時的なユーザー情報を作成する
        const tempUser = createTempUser(args.text)
        // 選択された値としてセット
        args.customItem = tempUser
      },
      [createTempUser]
    )

    return (
      <form className="flex flex-col gap-5 h-full">
        {/* モーダルの最小幅を制限し、画面の入力項目が正しく表示されるようにする */}
        <div className="py-2 px-1 min-w-[640px]">
          <div className="flex flex-col gap-y-2.5 py-2 px-1 overflow-y-auto">
            <p>{t('label.bulkUpdateSpeakersDialog.description')}</p>
            {/* 話者 */}
            <CrewSelectBoxField
              id="speakerId"
              name="speakerId"
              control={control}
              required={true}
              dataSource={speakerDataSource}
              labelMode="static"
              label={t('label.speechSpeaker')}
              valueExpr="id"
              displayExpr="displayName"
              searchExpr="displayName"
              searchEnabled={true}
              minSearchLength={0}
              acceptCustomValue={true}
              onCustomItemCreating={handleSpeakerCustomItemCreating}
              rules={validateRules.speakerId}
              onValueChanged={handleSpeakerIdChanged}
              selectedItem={selectedUser}
            />
          </div>
        </div>
        <div className="flex justify-between items-center mt-3">
          <div className="ml-auto flex gap-x-5">
            <CrewButton
              text={t('action.register')}
              type="primary"
              disabled={
                !canSend || isLoadingBulkUpdateEventRecordingTranscribes
              }
              onClick={handleSubmitButtonClick}
            />
            <CrewButton
              text={t('action.cancel')}
              type="normal"
              stylingMode="outlined"
              onClick={handleCancelButtonClick}
            />
          </div>
        </div>
      </form>
    )
  })
